import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  DialogContentText,
  LinearProgress,
} from "@mui/material";
import Webcam from "react-webcam";
import axios from "axios";
import useRequestIdGenerater from "customhooks";
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp";

const UploadProfilePicture = ({ open, onClose, getAlumniDetails }) => {
  const [picture, setPicture] = useState(null);
  const [isMirrored, setIsMirrored] = useState(true);
  const [activeTab, setActiveTab] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filePathName, setFilePathName] = useState("");
  const [syncPrompt, setSyncPrompt] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const webcamRef = useRef(null);
  const CHUNK_SIZE = 1 * 900 * 900;

  const RequestId = useRequestIdGenerater();
  const { generateTimeStamp, rendomRequestId } = useTimeStamp();

  const savedLinkedinToken = localStorage.getItem("linkedinToken");
  const savedGoogleToken = localStorage.getItem("goggleToken");

  const videoConstraints = {
    width: 250,
    height: 180,
    facingMode: "user",
  };

  const capture = () => {
    const pictureSrc = webcamRef.current.getScreenshot();
    if (pictureSrc) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.src = pictureSrc;

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        if (isMirrored) {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        }

        ctx.drawImage(img, 0, 0, img.width, img.height);

        canvas.toBlob((blob) => {
          const file = new File([blob], "captured_image.jpg", {
            type: "image/jpeg",
          });
          setPicture(file);
          handleFileChange({ target: { files: [file] } });
        }, "image/jpeg");
      };
    }
  };

  const uploadChunk = async (file, chunk, chunkIndex, totalChunks) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", chunk);
    formData.append("chunkIndex", chunkIndex);
    formData.append("totalChunks", totalChunks);
    formData.append("fileName", file.name);

    const response = await axios.post(
      "/v1/upload",
      formData,
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
          "Content-Type": "multipart/form-data",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
          ),
        },
      }
    );

    if (response?.data && chunkIndex === totalChunks - 1) {
      setUploading(false);
      const path = response?.data?.data?.filePath;
      setFilePathName(path);
      return path;
    }
  };

  const uploadFile = async (file, skipPreview = false) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uploadedPath = "";

    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const path = await uploadChunk(file, chunk, i, totalChunks);
      setUploadProgress(((i + 1) / totalChunks) * 100);

      if (path) uploadedPath = path;
    }

    if (!skipPreview) {
      setPicture(file);
    }

    return uploadedPath;
  };


  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      setPicture(file);
      uploadFile(file);
    } else {
      alert("Please upload a valid image file (JPEG or PNG). 🙏");
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPicture(null);
  };

  const handleImageUpload = async () => {
    if (!filePathName) return;
    setIsSubmitting(true);
    const payload = { profile: filePathName };

    try {
      await axios.post("/v1/alumni/upload/avatar-by-path", payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
          ),
        },
      });

      setPicture(null);
      onClose();
      getAlumniDetails();
      generateTimeStamp();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const syncLinkedInPicture = async () => {
    try {
      const res = await axios.get("/v1/auth/linkedin-profile", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("serviceToken")}`,
          "Content-Type": "application/json",
          token: savedLinkedinToken,
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
          ),
        },
      });

      const pictureUrl = res?.data?.data?.body?.picture;
      const response = await fetch(pictureUrl);
      const blob = await response.blob();
      const file = new File([blob], "linkedin_image.jpg", { type: blob.type });

      const filePath = await uploadFile(file, true);
      if (filePath) {
        setFilePathName(filePath);
        await handleImageUpload();
      }
    } catch (err) {
      console.error("Failed to sync from LinkedIn", err);
    }
  };

  const syncGooglePicture = async () => {
    try {
      const res = await axios.get(
        `/v1/auth/google/user?token=${savedGoogleToken}`,
        {
          headers: {
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
            ),
          },
        }
      );

      const pictureUrl = res?.data?.picture;
      const response = await fetch(pictureUrl);
      const blob = await response.blob();
      const file = new File([blob], "google_image.jpg", { type: blob.type });

      const filePath = await uploadFile(file, true);
      if (filePath) {
        setFilePathName(filePath);
        await handleImageUpload();
      }
    } catch (err) {
      console.error("Failed to sync from Google", err);
    }
  };

  useEffect(() => {
    if (open) {
      if (savedLinkedinToken) {
        setSyncPrompt("linkedin");
      } else if (savedGoogleToken) {
        setSyncPrompt("google");
      }
    }
  }, [open]);

  console.log(syncPrompt, 'syncc>>>>>>')

  useEffect(() => {
    if (syncPrompt === "linkedin" || syncPrompt === "google") {
      handleImageUpload()
    }
  }, [filePathName])

  const handleSyncYes = async () => {
    if (syncPrompt === "linkedin") {
      await syncLinkedInPicture();
    } else if (syncPrompt === "google") {
      await syncGooglePicture();
    }
    // setSyncPrompt(null);
  };

  const handleSyncNo = () => {
    setSyncPrompt(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Upload Profile Picture</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1">Tips for a Good Picture:</Typography>
              <Typography variant="body2">✅ Face should be clear</Typography>
              <Typography variant="body2">✅ Good lighting</Typography>
              <Typography variant="body2">✅ Clean background</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Tabs value={activeTab} onChange={handleTabChange} centered>
                <Tab label="Camera" value="camera" />
                <Tab label="Upload" value="upload" />
              </Tabs>

              {picture ? (
                <>
                  <img
                    src={URL.createObjectURL(picture)}
                    alt="Preview"
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 1 }}
                    onClick={() => setPicture(null)}
                  >
                    Retake / Upload Again
                  </Button>
                </>
              ) : activeTab === "camera" ? (
                <>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    videoConstraints={videoConstraints}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      transform: isMirrored ? "scaleX(-1)" : "none",
                    }}
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={capture}
                    sx={{ mt: 1 }}
                  >
                    Capture
                  </Button>
                </>
              ) : (
                <Stack spacing={2}>
                  <Button variant="outlined" component="label">
                    Upload from Gallery
                    <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                  </Button>
                </Stack>
              )}
              {uploading && (
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 2 }} />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!filePathName || uploading || isSubmitting}
            onClick={handleImageUpload}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {syncPrompt && (
        <Dialog open>
          <DialogTitle>Sync Profile Picture</DialogTitle>
          <DialogContent>
            <DialogContentText>
              We noticed you logged in via <strong>{syncPrompt}</strong>. <br />
              Do you want to sync your profile picture from {syncPrompt}? 😃
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSyncNo}>No</Button>
            <Button onClick={handleSyncYes} variant="contained">
              Yes, Sync
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default UploadProfilePicture;
